import { AmountSuperScript } from "components/design/AmountSuperScript";
import { useWindowSize } from "hooks/useWindowSize";
import { MobileLineItemRow } from "./MobileLineItemRow";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import FocusBlock from "components/DesignSystem/FocusBlock/FocusBlock";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import Table from "components/DesignSystem/Table/V2/Table";
import { DeleteIcon } from "components/icons/delete";
import { Duplicate } from "components/icons/Duplicate";
import { EditPencil } from "components/icons/EditPencil";
import { FOCUS_BLOCK_PLACEMENT } from "constants/focusBlock";
import {
  WRITE_PERMISSION_KEYS,
  WritePermissionKey,
} from "constants/permissions";
import { useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  useReactTable,
} from "react-table-8.10.7";
import { Invoice, InvoiceItem } from "types/Models/invoice";
import { VendorBill } from "types/Models/vendorBills";
import { formatDate } from "utils/formatDate";
import { LineItemBulkCategoryBar } from "./LineItemBulkCategoryBar";

const EMPTY_ROOT_CATEGORY_IDENTIFIERS: string[] = [];
const SELECT_COLUMN_SIZE = 5;
const SELECT_COLUMN_WIDTH_PX = 32;
const SELECT_COLUMN_WIDTH_STYLE = {
  width: SELECT_COLUMN_WIDTH_PX,
  minWidth: SELECT_COLUMN_WIDTH_PX,
  maxWidth: SELECT_COLUMN_WIDTH_PX,
};
const SELECT_COLUMN_CLASSNAME = "!t-px-2";

export const lineItemColumns = ({
  currency,
  showCategory = true,
  descriptionSize = 40,
  showProductCategoryTooltip = false,
}: {
  currency?: string;
  showCategory?: boolean;
  descriptionSize?: number;
  showProductCategoryTooltip?: boolean;
}) => {
  const createColumn = createColumnHelper<InvoiceItem>();

  return [
    createColumn.accessor("description", {
      size: descriptionSize,
      header: "ITEM",
      cell: (info) => {
        const {
          billing_period_enabled,
          billing_period_start_date,
          billing_period_end_date,
          category,
          classification,
          tax_rate,
          product_data_id,
        } = info.row.original;

        let categoryTooltip = category?.name;
        if (showProductCategoryTooltip && product_data_id) {
          categoryTooltip =
            "Category for a product's line item can only be changed from My Products";
        }

        return (
          <div className="t-flex t-flex-col t-gap-0.5">
            <span>{info.getValue()}</span>
            {billing_period_enabled &&
              billing_period_end_date &&
              billing_period_start_date && (
                <span className="t-text-text-30 t-text-body-sm">
                  {formatDate(billing_period_start_date)} -{" "}
                  {formatDate(billing_period_end_date)}
                </span>
              )}
            {showCategory && category && category.name && (
              <div className="t-text-body-sm t-text-text-30 t-mt-1">
                <ConditionalToolTip condition={categoryTooltip}>
                  <div className="t-flex t-gap-1">
                    <span>Category:</span>
                    <span className="t-text-text-60 t-truncate t-max-w-40">
                      {category.name}
                    </span>
                  </div>
                </ConditionalToolTip>
                {classification && classification.length > 0 && (
                  <div>
                    Classification:{" "}
                    <div className="t-text-text-60">
                      {classification.map((group) =>
                        group.classes.map((cls) => (
                          <div key={cls.uuid}>
                            {group.name}: {cls.name}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {tax_rate && (
                  <div>
                    Tax:{" "}
                    <span className="t-text-text-60">{tax_rate.rate}%</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    }),
    createColumn.accessor("quantity", {
      size: 10,
      header: "QUANTITY",
    }),
    createColumn.accessor("rate", {
      size: 20,
      header: "UNIT PRICE",
      cell: (info) => (
        <span className="t-flex t-items-center t-gap-1">
          <AmountSuperScript
            amount={Number(info.getValue() || 0)}
            currencyCode={currency}
          />
        </span>
      ),
    }),
    createColumn.accessor("amount", {
      size: 20,
      header: "AMOUNT",
      cell: (info) => (
        <span className="t-flex t-items-center t-gap-1">
          <AmountSuperScript
            amount={Number(info.getValue() || 0)}
            currencyCode={currency}
          />
        </span>
      ),
    }),
  ];
};

export const LineItemTable = ({
  invoice,
  handleItemDelete,
  handleItemEdit,
  handleItemDuplicate,
  invoiceItem,
  renderEditForm,
  isUpdatingTableItem = false,
  showCategory = true,
  showTotals = false,
  rootCategoryIdentifiers = EMPTY_ROOT_CATEGORY_IDENTIFIERS,
  onBulkCategoryUpdate,
  writePermissionKey = WRITE_PERMISSION_KEYS.REVENUE,
}: {
  invoice: Invoice | VendorBill;
  handleItemDelete?: (index: number) => void;
  handleItemEdit?: (item: InvoiceItem) => void;
  handleItemDuplicate?: (item: InvoiceItem) => void;
  invoiceItem?: InvoiceItem | null;
  renderEditForm?: (item: InvoiceItem, index?: number) => React.ReactNode;
  isUpdatingTableItem?: boolean;
  showCategory?: boolean;
  showTotals?: boolean;
  rootCategoryIdentifiers?: string[];
  onBulkCategoryUpdate?: (items: InvoiceItem[]) => Promise<void> | void;
  writePermissionKey?: WritePermissionKey;
}) => {
  const { isMobile } = useWindowSize();
  const [deletedItemId, setDeletedItemId] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const createColumn = createColumnHelper<InvoiceItem>();
  const currency = invoice?.currency;
  const enableBulkCategory = Boolean(onBulkCategoryUpdate);
  const descriptionSize = enableBulkCategory ? 40 - SELECT_COLUMN_SIZE : 40;

  const columns = lineItemColumns({
    currency,
    showCategory,
    descriptionSize,
    showProductCategoryTooltip: enableBulkCategory,
  });

  const invoiceItems = (invoice?.invoice_items as InvoiceItem[]) || [];
  const selectedUuids = Object.keys(rowSelection);

  const handleBulkCategoryApply = async (categoryId: string) => {
    const selectedUuidSet = new Set(selectedUuids);
    const updatedItems = invoiceItems.map((item) =>
      selectedUuidSet.has(item.uuid)
        ? { ...item, category_id: categoryId }
        : item
    );

    await onBulkCategoryUpdate?.(updatedItems);
    setRowSelection({});
  };
  const totalQuantity = invoiceItems.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  );
  const totalAmount = invoiceItems.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const totalsRow = showTotals ? (
    <Table.Row className="!t-border-b-0">
      {enableBulkCategory && (
        <Table.Cell
          className={SELECT_COLUMN_CLASSNAME}
          style={SELECT_COLUMN_WIDTH_STYLE}
        />
      )}
      <Table.Cell style={{ width: `${descriptionSize}%` }}>
        <span className="t-text-body-sm t-font-semibold t-text-text-100">
          Total
        </span>
      </Table.Cell>
      <Table.Cell style={{ width: "10%" }}>
        <span className="t-text-body-sm t-font-semibold t-text-text-100">
          {totalQuantity}
        </span>
      </Table.Cell>
      <Table.Cell style={{ width: "20%" }} />
      <Table.Cell style={{ width: "20%" }}>
        <span className="t-text-body-sm t-font-semibold t-text-text-100">
          <AmountSuperScript amount={totalAmount} currencyCode={currency} />
        </span>
      </Table.Cell>
      <Table.Cell style={{ width: "10%" }} />
    </Table.Row>
  ) : null;

  const selectColumn = createColumn.display({
    id: "select",
    size: SELECT_COLUMN_SIZE,
    header: ({ table }) => (
      <SelectionHeaderCell table={table} ariaLabel="Select all line items" />
    ),
    cell: ({ row }) => (
      <SelectionCell
        row={row}
        ariaLabel={`Select line item ${row.original.description}`}
      />
    ),
  });

  const actionColumn = createColumn.display({
    id: "actions",
    size: 10,
    header: "",
    cell: (info) => {
      const { uuid } = info.row.original;
      return (
        <div className="t-flex t-gap-1">
          <ToolTip text="Duplicate">
            <Button
              size="small"
              type="button"
              customType="ghost"
              className="sm:!t-px-2"
              onClick={() => {
                handleItemDuplicate?.(info.row.original);
              }}
            >
              <span className="t-text-text-30">
                <Duplicate />
              </span>
            </Button>
          </ToolTip>
          <div>
            <FocusBlock.Trigger asChild>
              <Button
                size="small"
                type="button"
                customType="ghost"
                className="sm:!t-px-2"
                onClick={() => {
                  handleItemEdit && handleItemEdit(info.row.original);
                }}
              >
                <span className="t-text-text-30">
                  <EditPencil />
                </span>
              </Button>
            </FocusBlock.Trigger>
          </div>
          <Button
            size="small"
            type="button"
            customType="ghost"
            className="sm:!t-px-2"
            isLoading={deletedItemId === uuid && isUpdatingTableItem}
            disabled={deletedItemId === uuid && isUpdatingTableItem}
            onClick={() => {
              handleItemDelete && handleItemDelete(info.row.index);
              setDeletedItemId(uuid);
            }}
          >
            {deletedItemId === uuid && isUpdatingTableItem ? (
              ""
            ) : (
              <span className="t-text-text-30">
                <DeleteIcon stroke="1.2" />
              </span>
            )}
          </Button>
        </div>
      );
    },
  });

  const table = useReactTable({
    columns: enableBulkCategory
      ? [selectColumn, ...columns, actionColumn]
      : [...columns, actionColumn],
    data: invoiceItems,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.uuid,
    enableRowSelection: enableBulkCategory
      ? (row) => !row.original.product_data_id
      : false,
    getCoreRowModel: getCoreRowModel(),
  });

  const mobileTotals = showTotals ? (
    <div className="t-flex t-items-center t-justify-between t-gap-2 t-py-3 t-text-body-sm t-font-semibold t-text-text-100">
      <span>Total</span>
      <span>Qty {totalQuantity}</span>
      <AmountSuperScript amount={totalAmount} currencyCode={currency} />
    </div>
  ) : null;

  const mobileList = (
    <div className="t-flex t-flex-col">
      {table.getRowModel().rows.map((row) => (
        <FocusBlock.Item
          key={row.id}
          value={`line-item-row-${row.id}`}
          placement={FOCUS_BLOCK_PLACEMENT.REPLACE}
        >
          <MobileLineItemRow row={row} />
          <FocusBlock.Content>
            {renderEditForm && renderEditForm(row.original, row.index)}
          </FocusBlock.Content>
        </FocusBlock.Item>
      ))}
      {mobileTotals}
    </div>
  );

  return (
    <>
      {enableBulkCategory && selectedUuids.length > 0 && (
        <LineItemBulkCategoryBar
          selectedCount={selectedUuids.length}
          rootCategoryIdentifiers={rootCategoryIdentifiers}
          writePermissionKey={writePermissionKey}
          isApplying={isUpdatingTableItem}
          onApply={handleBulkCategoryApply}
          onCancel={() => setRowSelection({})}
        />
      )}
      {isMobile && mobileList}
      {!isMobile && (
        <TableUI
          size="regular"
          table={table}
          moreRows={totalsRow}
          header={
            enableBulkCategory
              ? ({ headerGroup }) => (
                  <Table.HeadRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.HeadCell
                        key={header.id}
                        colSpan={header.colSpan}
                        className={
                          header.column.id === "select"
                            ? SELECT_COLUMN_CLASSNAME
                            : undefined
                        }
                        style={
                          header.column.id === "select"
                            ? SELECT_COLUMN_WIDTH_STYLE
                            : { width: `${header.getSize()}%` }
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    ))}
                  </Table.HeadRow>
                )
              : undefined
          }
          children={({ row }) => {
            const item = row.original;
            return (
              <Table.Row key={row.id}>
                <FocusBlock.Item
                  value={`line-item-row-${row.id}`}
                  placement={FOCUS_BLOCK_PLACEMENT.REPLACE}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      className={
                        cell.column.id === "select"
                          ? SELECT_COLUMN_CLASSNAME
                          : undefined
                      }
                      style={
                        cell.column.id === "select"
                          ? SELECT_COLUMN_WIDTH_STYLE
                          : undefined
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                  <FocusBlock.Content asChild>
                    <td colSpan={row.getVisibleCells().length}>
                      {renderEditForm && renderEditForm(item, row.index)}
                    </td>
                  </FocusBlock.Content>
                </FocusBlock.Item>
              </Table.Row>
            );
          }}
        />
      )}
    </>
  );
};
